/**
 * SyncStatusIndicator
 *
 * Visual indicator showing offline/syncing/pending status.
 * Displays in the app topbar to show users the current sync state.
 * When there are local changes or sync failures, clicking opens a menu
 * with options to retry or discard changes.
 */

import { FC, useMemo, useState } from 'react';

import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Badge,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';

import { useTranslation } from 'common/hooks/useTranslation';
import { useSyncStatus, SyncStatus } from 'app/providers/SyncStatusProvider';
import { DiscardChangesDialog } from './DiscardChangesDialog';

type SyncStatusConfig = {
  icon: FC<{ sx?: SxProps<Theme> }>;
  color: string;
  labelKey: string;
  showBadge: boolean;
  showProgress: boolean;
};

const STATUS_CONFIG: Record<SyncStatus, SyncStatusConfig> = {
  online: {
    icon: CloudDoneIcon,
    color: '#4caf50', // green
    labelKey: 'sync.status.online',
    showBadge: false,
    showProgress: false,
  },
  offline: {
    icon: CloudOffIcon,
    color: '#ff9800', // orange
    labelKey: 'sync.status.offline',
    showBadge: true,
    showProgress: false,
  },
  syncing: {
    icon: CloudSyncIcon,
    color: '#2196f3', // blue
    labelKey: 'sync.status.syncing',
    showBadge: true,
    showProgress: true,
  },
  pending: {
    icon: CloudQueueIcon,
    color: '#ff9800', // orange
    labelKey: 'sync.status.pending',
    showBadge: true,
    showProgress: false,
  },
  error: {
    icon: ErrorOutlineIcon,
    color: '#f44336', // red
    labelKey: 'sync.status.error',
    showBadge: true,
    showProgress: false,
  },
};

const indicatorStyles: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const progressStyles: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-12px',
  marginLeft: '-12px',
};

const menuItemErrorStyles: SxProps<Theme> = {
  color: 'error.main',
  '& .MuiListItemIcon-root': {
    color: 'error.main',
  },
};

export type SyncStatusIndicatorProps = {
  /** Called when indicator is clicked */
  onClick?: () => void;
};

export const SyncStatusIndicator: FC<SyncStatusIndicatorProps> = ({ onClick }) => {
  const { t } = useTranslation();
  const {
    status,
    pendingCount,
    failedCount,
    syncProgress,
    retryFailed,
    hasLocalChanges,
    discardLocalChanges,
    isOnline,
  } = useSyncStatus();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  const tooltipText = useMemo(() => {
    const baseText = t(config.labelKey);

    if (status === 'syncing' && syncProgress) {
      return `${baseText} (${syncProgress.completed}/${syncProgress.total})`;
    }

    if (status === 'pending' || (status === 'offline' && pendingCount > 0)) {
      return `${baseText} (${pendingCount})`;
    }

    if (status === 'error') {
      return `${baseText} (${failedCount} failed)`;
    }

    return baseText;
  }, [status, config.labelKey, pendingCount, failedCount, syncProgress, t]);

  const badgeContent = useMemo(() => {
    if (status === 'error') {
      return failedCount;
    }
    if (status === 'pending' || status === 'syncing' || status === 'offline') {
      return pendingCount;
    }
    return 0;
  }, [status, pendingCount, failedCount]);

  // Show menu when there are local changes (pending or failed)
  const shouldShowMenu = hasLocalChanges && status !== 'syncing';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (shouldShowMenu) {
      setMenuAnchor(event.currentTarget);
    } else if (status === 'error') {
      retryFailed();
    }
    onClick?.();
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleRetry = () => {
    handleCloseMenu();
    retryFailed();
  };

  const handleOpenDiscardDialog = () => {
    handleCloseMenu();
    setDiscardDialogOpen(true);
  };

  const handleCloseDiscardDialog = () => {
    setDiscardDialogOpen(false);
  };

  const handleConfirmDiscard = async () => {
    await discardLocalChanges();
  };

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <IconButton onClick={handleClick} size="small" sx={indicatorStyles}>
          <Badge
            badgeContent={config.showBadge ? badgeContent : 0}
            color={status === 'error' ? 'error' : 'primary'}
            max={99}
          >
            <Icon sx={{ color: config.color, fontSize: 24 }} />
          </Badge>
          {config.showProgress && (
            <CircularProgress
              size={24}
              sx={{ ...progressStyles, color: config.color }}
              variant={syncProgress ? 'determinate' : 'indeterminate'}
              value={syncProgress ? (syncProgress.completed / syncProgress.total) * 100 : undefined}
            />
          )}
        </IconButton>
      </Tooltip>

      {/* Menu for pending/failed changes */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        {failedCount > 0 && isOnline && (
          <MenuItem onClick={handleRetry}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('sync.menu.retry')}</ListItemText>
          </MenuItem>
        )}
        {failedCount > 0 && isOnline && <Divider />}
        <MenuItem onClick={handleOpenDiscardDialog} sx={menuItemErrorStyles}>
          <ListItemIcon>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('sync.menu.discard')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Discard confirmation dialog */}
      <DiscardChangesDialog
        open={discardDialogOpen}
        onClose={handleCloseDiscardDialog}
        onConfirm={handleConfirmDiscard}
        pendingCount={pendingCount}
        failedCount={failedCount}
      />
    </>
  );
};
